import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, ScrollRestoration } from 'react-router-dom';
import { Toaster } from 'sonner';

// Scroll Management
import ScrollToTop from './components/ScrollToTop';
import SaveScrollPosition from './components/SaveScrollPosition';

// Customer Pages
import Home from './pages/Home';
import Shop from './pages/Shop';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Contact from './pages/Contact';
import About from './pages/About';
import FAQ from './pages/FAQ';
import TrackOrder from './pages/TrackOrder';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminItems from './pages/admin/AdminItems';
import AdminCategories from './pages/admin/AdminCategories';
import AdminReviews from './pages/admin/AdminReviews';
import AdminInvoices from './pages/admin/AdminInvoices';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminContacts from './pages/admin/AdminContacts';
import AdminPayments from './pages/admin/AdminPayments';
import AdminUsers from './pages/admin/AdminUsers';
import AdminCoupons from './pages/admin/AdminCoupons';
import AdminCarousel from './pages/admin/AdminCarousel';

// Layout
import CustomerLayout from './components/layout/CustomerLayout';
import AdminLayout from './components/layout/AdminLayout';
import ProtectedRoute from './components/ProtectedRoute';

// Subdomain detection for admin panel
const isAdminSubdomain = () => {
  const hostname = window.location.hostname;
  return hostname.startsWith('admin.') || hostname.startsWith('admin-');
};

// Customer Routes Component
const CustomerRoutes = () => (
  <Routes>
    <Route element={<CustomerLayout />}>
      <Route path="/" element={<Home />} />
      <Route path="/shop" element={<Shop />} />
      <Route path="/shop/:categoryId" element={<Shop />} />
      <Route path="/product/:id" element={<ProductDetail />} />
      <Route path="/cart" element={<Cart />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/about" element={<About />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/track-order" element={<TrackOrder />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
    </Route>
    {/* Redirect admin routes to admin subdomain in production */}
    <Route path="/admin/*" element={<Navigate to="/" replace />} />
  </Routes>
);

// Admin Routes Component (for subdomain)
const AdminRoutes = () => (
  <Routes>
    <Route path="/login" element={<AdminLogin />} />
    <Route path="/" element={<Navigate to="/login" replace />} />
    <Route element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
      <Route path="/dashboard" element={<AdminDashboard />} />
      <Route path="/items" element={<AdminItems />} />
      <Route path="/categories" element={<AdminCategories />} />
      <Route path="/reviews" element={<AdminReviews />} />
      <Route path="/invoices" element={<AdminInvoices />} />
      <Route path="/customers" element={<AdminCustomers />} />
      <Route path="/contacts" element={<AdminContacts />} />
      <Route path="/payments" element={<AdminPayments />} />
      <Route path="/users" element={<AdminUsers />} />
      <Route path="/coupons" element={<AdminCoupons />} />
      <Route path="/carousel" element={<AdminCarousel />} />
    </Route>
    {/* Catch all - redirect to dashboard */}
    <Route path="*" element={<Navigate to="/dashboard" replace />} />
  </Routes>
);

function App() {
  const isAdmin = isAdminSubdomain();
  
  return (
    <Router>
      <ScrollToTop />
      <SaveScrollPosition />
      <Toaster 
        position="top-right" 
        richColors 
        toastOptions={{
          style: {
            fontFamily: 'Lato, sans-serif',
          },
        }}
      />
      <div className="grain-overlay" />
      
      {isAdmin ? (
        // Admin subdomain routes (admin.yourdomain.com)
        <AdminRoutes />
      ) : (
        // Main domain routes with /admin fallback for development
        <Routes>
          {/* Customer Routes */}
          <Route element={<CustomerLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/shop/:categoryId" element={<Shop />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/track-order" element={<TrackOrder />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
          </Route>
          
          {/* Admin Routes (fallback for development/preview) */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/items" element={<AdminItems />} />
            <Route path="/admin/categories" element={<AdminCategories />} />
            <Route path="/admin/reviews" element={<AdminReviews />} />
            <Route path="/admin/invoices" element={<AdminInvoices />} />
            <Route path="/admin/customers" element={<AdminCustomers />} />
            <Route path="/admin/contacts" element={<AdminContacts />} />
            <Route path="/admin/payments" element={<AdminPayments />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/coupons" element={<AdminCoupons />} />
            <Route path="/admin/carousel" element={<AdminCarousel />} />
          </Route>
        </Routes>
      )}
    </Router>
  );
}

export default App;
